import React, { useState, useEffect, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Box,
  Typography,
  IconButton,
  Avatar,
  Slide,
  Grow,
  Fade,
  useTheme,
  alpha,
  Button,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  LocalHospital as MedicalIcon,
  Payment as PaymentIcon,
  Message as MessageIcon,
  Event as EventIcon,
  Notifications as NotificationIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const TOAST_TYPES = {
  APPOINTMENT: { 
    icon: EventIcon, 
    color: '#2196f3', 
    severity: 'info',
    defaultDuration: 6000
  },
  PAYMENT: { 
    icon: PaymentIcon, 
    color: '#4caf50', 
    severity: 'success',
    defaultDuration: 5000
  },
  MESSAGE: { 
    icon: MessageIcon, 
    color: '#ff9800', 
    severity: 'info',
    defaultDuration: 7000
  },
  MEDICAL: { 
    icon: MedicalIcon, 
    color: '#f44336', 
    severity: 'warning',
    defaultDuration: 8000
  },
  SUCCESS: { 
    icon: CheckCircleIcon, 
    color: '#4caf50', 
    severity: 'success',
    defaultDuration: 4000
  },
  WARNING: { 
    icon: WarningIcon, 
    color: '#ff5722', 
    severity: 'warning',
    defaultDuration: 6000
  },
  ERROR: { 
    icon: ErrorIcon, 
    color: '#f44336', 
    severity: 'error',
    defaultDuration: 8000
  },
  INFO: { 
    icon: InfoIcon, 
    color: '#2196f3', 
    severity: 'info',
    defaultDuration: 5000
  }
};

const ROLE_SPECIFIC_SOUNDS = {
  patient: {
    APPOINTMENT: '/sounds/patient-appointment.mp3',
    PAYMENT: '/sounds/payment-success.mp3',
    MESSAGE: '/sounds/message-received.mp3',
    default: '/sounds/notification-gentle.mp3'
  },
  doctor: {
    APPOINTMENT: '/sounds/doctor-appointment.mp3',
    MESSAGE: '/sounds/message-urgent.mp3',
    MEDICAL: '/sounds/medical-alert.mp3',
    default: '/sounds/notification-professional.mp3'
  },
  receptionist: {
    APPOINTMENT: '/sounds/appointment-booking.mp3',
    PAYMENT: '/sounds/payment-received.mp3',
    default: '/sounds/notification-office.mp3'
  },
  admin: {
    ERROR: '/sounds/error-alert.mp3',
    WARNING: '/sounds/warning-alert.mp3',
    default: '/sounds/notification-admin.mp3'
  }
};

const NotificationToast = ({ 
  open, 
  notification, 
  onClose, 
  userRole = 'patient',
  position = { vertical: 'top', horizontal: 'right' },
  enableSound = true,
  enableAnimation = true
}) => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  const notificationType = notification?.type || 'INFO';
  const config = TOAST_TYPES[notificationType] || TOAST_TYPES.INFO;
  const IconComponent = config.icon;
  const duration = notification?.duration || config.defaultDuration;

  // Progress bar animation
  useEffect(() => {
    if (open && duration > 0) {
      setProgress(100);
      setIsVisible(true);

      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);

        if (remaining <= 0) {
          clearInterval(interval);
          handleClose();
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [open, duration]);

  // Sound effect
  const playNotificationSound = useCallback(() => {
    if (!enableSound) return;

    try {
      const roleSounds = ROLE_SPECIFIC_SOUNDS[userRole] || ROLE_SPECIFIC_SOUNDS.patient;
      const soundPath = roleSounds[notificationType] || roleSounds.default;
      
      const audio = new Audio(soundPath);
      audio.volume = 0.4;
      audio.play().catch(() => {
        // Fallback to default notification sound
        const fallbackAudio = new Audio('/sounds/notification-default.mp3');
        fallbackAudio.volume = 0.3;
        fallbackAudio.play().catch(() => {
          // Silent failure - no audio support or user interaction required
        });
      });
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }, [enableSound, userRole, notificationType]);

  useEffect(() => {
    if (open && notification) {
      playNotificationSound();
    }
  }, [open, notification, playNotificationSound]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  }, [onClose]);

  const handleActionClick = useCallback(() => {
    if (notification?.link_url) {
      window.location.href = notification.link_url;
    }
    handleClose();
  }, [notification?.link_url, handleClose]);

  if (!open || !notification) {
    return null;
  }

  const TransitionComponent = enableAnimation ? Slide : Fade;
  const transitionProps = enableAnimation 
    ? { direction: position.horizontal === 'right' ? 'left' : 'right' }
    : {};

  return (
    <AnimatePresence>
      {isVisible && (
        <Snackbar
          open={isVisible}
          anchorOrigin={position}
          onClose={handleClose}
          TransitionComponent={TransitionComponent}
          TransitionProps={transitionProps}
          sx={{
            '& .MuiSnackbar-root': {
              position: 'fixed',
            }
          }}
        >
          <motion.div
            initial={enableAnimation ? { opacity: 0, scale: 0.8, y: -20 } : { opacity: 1 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 30,
              duration: 0.3 
            }}
          >
            <Alert
              severity={config.severity}
              onClose={handleClose}
              sx={{
                minWidth: 320,
                maxWidth: 400,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${alpha(config.color, 0.3)}`,
                borderLeft: `4px solid ${config.color}`,
                boxShadow: theme.shadows[8],
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                '& .MuiAlert-icon': {
                  color: config.color,
                  fontSize: '1.5rem',
                  alignItems: 'center'
                },
                '& .MuiAlert-action': {
                  alignItems: 'flex-start',
                  paddingTop: 1
                }
              }}
              icon={
                <Avatar
                  sx={{
                    backgroundColor: alpha(config.color, 0.1),
                    color: config.color,
                    width: 32,
                    height: 32
                  }}
                >
                  <IconComponent fontSize="small" />
                </Avatar>
              }
              action={
                <Box display="flex" alignItems="center" gap={0.5}>
                  {notification.link_url && (
                    <IconButton
                      size="small"
                      onClick={handleActionClick}
                      sx={{
                        color: config.color,
                        '&:hover': {
                          backgroundColor: alpha(config.color, 0.1)
                        }
                      }}
                    >
                      <OpenIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.text.secondary, 0.1)
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              <AlertTitle
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  marginBottom: 0.5,
                  color: theme.palette.text.primary
                }}
              >
                {notification.title}
              </AlertTitle>
              
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.4,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {notification.body}
              </Typography>

              {notification.link_url && (
                <Box mt={1}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleActionClick}
                    sx={{
                      color: config.color,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      padding: '2px 8px',
                      minHeight: 'auto',
                      '&:hover': {
                        backgroundColor: alpha(config.color, 0.1)
                      }
                    }}
                    endIcon={<OpenIcon sx={{ fontSize: '0.75rem' }} />}
                  >
                    View Details
                  </Button>
                </Box>
              )}

              {/* Progress bar */}
              {duration > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    backgroundColor: alpha(config.color, 0.2)
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      backgroundColor: config.color,
                      width: `${progress}%`,
                      transition: 'width 0.05s linear',
                      borderRadius: '0 0 8px 8px'
                    }}
                  />
                </Box>
              )}
            </Alert>
          </motion.div>
        </Snackbar>
      )}
    </AnimatePresence>
  );
};

// Toast Manager Component for handling multiple toasts
export const NotificationToastManager = ({ 
  userRole = 'patient',
  maxToasts = 3,
  enableSound = true,
  enableAnimation = true 
}) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      ...notification,
      timestamp: Date.now()
    };

    setToasts(prev => {
      const updated = [newToast, ...prev];
      // Limit the number of simultaneous toasts
      return updated.slice(0, maxToasts);
    });

    return id;
  }, [maxToasts]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Expose methods to parent components
  useEffect(() => {
    window.showNotificationToast = addToast;
    window.clearNotificationToasts = clearAllToasts;

    return () => {
      delete window.showNotificationToast;
      delete window.clearNotificationToasts;
    };
  }, [addToast, clearAllToasts]);

  return (
    <Stack
      spacing={1}
      sx={{
        position: 'fixed',
        top: 24,
        right: 24,
        zIndex: theme => theme.zIndex.snackbar + 1,
        pointerEvents: 'none'
      }}
    >
      <AnimatePresence>
        {toasts.map((toast, index) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, x: 300, scale: 0.8 }}
            animate={{ 
              opacity: 1, 
              x: 0, 
              scale: 1,
              y: index * -10 // Slight stacking effect
            }}
            exit={{ opacity: 0, x: 300, scale: 0.8 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 30,
              delay: index * 0.1 
            }}
            style={{ pointerEvents: 'auto' }}
          >
            <NotificationToast
              open={true}
              notification={toast}
              onClose={() => removeToast(toast.id)}
              userRole={userRole}
              position={{ vertical: 'top', horizontal: 'right' }}
              enableSound={enableSound && index === 0} // Only play sound for the first toast
              enableAnimation={enableAnimation}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </Stack>
  );
};

export default NotificationToast;
